/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { observer } from 'mobx-react-lite'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { rpMediaQueries } from '../../core/theme'
import { useRussianPokerStore } from '../../providers/RussianPokerProvider'
import { numberWithSpaces } from '../../utils/number'
import { DepositIcon, WalletIcon } from '../Icons'
import { Typography } from '../Typography'

const createBlinkAnimation = (baseColor: string, highlightColor: string) =>
  keyframes`
    0%,
    100% {
      color: ${baseColor};
      opacity: 0.9;
    }

    50% {
      color: ${highlightColor};
      opacity: 1;
    }
  `

const valueFlipAnimation = keyframes`
  0% {
    transform: translateY(0) rotateX(0deg);
    filter: blur(0);
  }

  50% {
    transform: translateY(-1px) rotateX(-16deg);
    filter: blur(0.4px);
  }

  100% {
    transform: translateY(0) rotateX(0deg);
    filter: blur(0);
  }
`

const BalanceButton = styled.button<{ $isDepositEmpty: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  padding: 3px 6px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.2s;
  position: relative;

  /* Увеличенная область клика для мобильных устройств */
  &::after {
    content: '';
    position: absolute;
    top: -12px;
    left: -12px;
    right: -12px;
    bottom: -12px;
  }

  ${rpMediaQueries.tablet} {
    padding: 5px 10px;
    gap: 5px;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.08);
  }

  ${({ $isDepositEmpty }) =>
    $isDepositEmpty &&
    css`
      border-color: rgba(255, 215, 0, 0.3);
    `}
`

const StyledWalletIcon = styled(WalletIcon, {
  shouldForwardProp: (prop) => prop !== '$isDepositEmpty',
})<{ $isDepositEmpty: boolean }>(({ $isDepositEmpty, theme }) =>
  $isDepositEmpty
    ? css`
        animation: ${createBlinkAnimation(
            theme.rpTheme.colors.white,
            theme.rpTheme.colors.gold1
          )}
          1.4s ease-in-out infinite;
      `
    : undefined
)

const RefillText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$isDepositEmpty',
})<{ $isDepositEmpty: boolean }>(({ $isDepositEmpty, theme }) => [
  css`
    color: ${theme.rpTheme.colors.white};
    transition: color 0.2s;
  `,
  $isDepositEmpty &&
    css`
      animation: ${createBlinkAnimation(
          theme.rpTheme.colors.white,
          theme.rpTheme.colors.gold1
        )}
        1.4s ease-in-out infinite;
    `,
])

const DepositValue = styled.span`
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  line-height: 1;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;

  & > span {
    display: inline-block;
    transform-origin: center;
    will-change: transform, filter;
    animation: ${valueFlipAnimation} 300ms linear;
  }
`

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0 3px;

  ${rpMediaQueries.tablet} {
    height: 16px;
    margin: 0 5px;
  }
`

const FLIP_DURATION_MS = 300

export const Balance: FC = observer(() => {
  const { t } = useTranslation()
  const { deposit, uiStore } = useRussianPokerStore()

  const [animatedDeposit, setAnimatedDeposit] = useState<number>(0)
  const [flipTick, setFlipTick] = useState(0)

  const animatedRef = useRef(0)
  const rafIdRef = useRef<number | null>(null)

  const stopRaf = useCallback(() => {
    if (rafIdRef.current !== null) {
      window.cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
  }, [])

  useEffect(() => {
    if (deposit === animatedRef.current) {
      return
    }

    const startValue = animatedRef.current
    const delta = deposit - startValue
    const startedAt = performance.now()

    const animate = (now: number) => {
      const elapsed = now - startedAt
      const progress = Math.min(1, elapsed / FLIP_DURATION_MS)
      const easedProgress = 1 - (1 - progress) ** 3
      const nextValue =
        progress >= 1 ? deposit : Math.round(startValue + delta * easedProgress)

      if (nextValue !== animatedRef.current) {
        animatedRef.current = nextValue
        setAnimatedDeposit(nextValue)

        // Не запускаем flip на финальном кадре, чтобы убрать визуальный "прыжок" в конце.
        if (progress < 1) {
          setFlipTick((prev) => prev + 1)
        }
      }

      if (progress < 1) {
        rafIdRef.current = window.requestAnimationFrame(animate)
      } else {
        stopRaf()
      }
    }

    stopRaf()
    rafIdRef.current = window.requestAnimationFrame(animate)
  }, [deposit, stopRaf])

  useEffect(() => {
    return () => {
      stopRaf()
    }
  }, [stopRaf])

  const isDepositEmpty = deposit === 0

  return (
    <BalanceButton
      $isDepositEmpty={isDepositEmpty}
      data-cy="rp-balance-refill-btn"
      onClick={() => {
        uiStore.partialUpdate({
          viewRefillPopup: true,
        })
      }}>
      <Typography as="span" variant="top">
        {t('balance')}:&nbsp;
      </Typography>
      <Typography as="span" variant="top" color="gold1">
        <DepositValue data-cy="rp-deposit-amount">
          <span key={flipTick}>{numberWithSpaces(animatedDeposit)}&nbsp;</span>
        </DepositValue>
      </Typography>
      <DepositIcon
        size={{
          mobile: '8px',
          tablet: '12px',
          desktop: '12px',
        }}
        color="gold1"
      />
      <Divider />
      <StyledWalletIcon
        color="white"
        $isDepositEmpty={isDepositEmpty}
        size={{
          mobile: '12px',
          tablet: '20px',
          desktop: '20px',
        }}
      />
      <RefillText $isDepositEmpty={isDepositEmpty} variant="top" as="span">
        {t('refill')}
      </RefillText>
    </BalanceButton>
  )
})
